import type {
  Product,
  SkuSpecificationsName,
} from 'src/components/restructure/product/types/product'
import { KEYWORDS_HIDDEN_COMPONENT } from 'src/components/restructure/product/sections/Sidebar/SkuSelector/DefaultSelector'
import { useEffect, useRef, useState } from 'react'
import { ChevronDownThinIcon } from 'src/components/Icons/ChevronDownThinIcon'
import { useClickAway } from 'react-use'

interface SkuOption {
  value: string
  skuId: string
  position: number | undefined
}

interface RowProps {
  label: string
  value: string
  hasOnlyOne: boolean
  options: SkuOption[]
}

const SkuSelector = ({
  product,
  filters = [],
  onSelect,
  selectedSku,
}: {
  product: Product
  filters?: string[]
  onSelect: React.Dispatch<React.SetStateAction<string | null>>
  selectedSku?: string | null
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const { items } = product
  const ref = useRef(null)

  useClickAway(ref, () => isOpen && setIsOpen(false))

  const availableItems = items.filter((item) => item.isAvailable)

  useEffect(() => {
    if (!selectedSku && availableItems.length > 0) {
      onSelect(availableItems[0].itemId)
    }
  }, [selectedSku, availableItems, onSelect])

  const availableSpecifications = items.reduce((acc, cur) => {
    if (!cur.isAvailable) return acc

    cur.variations.forEach((variation) => {
      const variationName = variation as SkuSpecificationsName

      if (filters.includes(variationName)) return

      const values = cur[variationName]

      if (!values || KEYWORDS_HIDDEN_COMPONENT.includes(values[0])) return

      if (!acc[variationName]) acc[variationName] = []
      if (acc[variationName].some((v) => v.value === values[0])) return

      const position = product.skuSpecifications
        .find((specification) => specification?.field?.name === variation)
        ?.values?.find((value) => value?.name === values?.[0])?.position

      acc[variationName].push({
        value: values[0],
        skuId: cur.itemId,
        position,
      })
    })

    return acc
  }, {} as Record<SkuSpecificationsName, SkuOption[]>)

  const specKeys = Object.keys(availableSpecifications)
  const hasNoSpecifications = specKeys.length === 0

  const renderSelectorRow = ({
    label,
    value,
    hasOnlyOne,
    options = [],
  }: RowProps) => {
    const isSingleValue = value.toLowerCase() === 'único'
    const displayText = isSingleValue ? `${label} único` : `${label}: ${value}`

    return (
      <div
        className={`flex flex-col h-full max-h-9 md:max-h-full bg-white relative ${
          hasOnlyOne
            ? 'opacity-80 md:border md:border-checkoutInputBorder'
            : 'border border-checkoutInputBorder'
        }`}
        key={`cross-sell-${label}`}
        ref={hasOnlyOne ? null : ref}
      >
        <button
          type="button"
          onClick={() => !hasOnlyOne && setIsOpen(!isOpen)}
          disabled={hasOnlyOne}
          className={`flex justify-between items-center py-[6px] md:py-5 w-full md:pl-4 md:pr-4 h-full ${
            hasOnlyOne ? 'cursor-default pl-0 pr-0' : 'cursor-pointer pl-4 pr-2'
          }`}
        >
          <p className="text-xs md:text-base text-neutral11 font-normal line-clamp-1">
            {displayText}
          </p>

          {!hasOnlyOne && (
            <div
              className={`-rotate-90 md:-rotate-0 ${
                isOpen ? 'md:-rotate-180' : ''
              }`}
            >
              <ChevronDownThinIcon />
            </div>
          )}
        </button>

        {isOpen && !hasOnlyOne && (
          <div className="pr-[6px] absolute z-[100] bottom-0 top-full -left-[1px] w-[calc(100%+2px)] h-fit bg-white border border-checkoutInputBorder shadow-lg">
            <ul className="sm:max-h-[210px] overflow-auto flex flex-col">
              {options.map(({ value: optValue, skuId, position }) => (
                <li
                  key={`cross-sell-select-${skuId}-${optValue}`}
                  style={{ order: position }}
                  className="last:pb-2"
                >
                  <button
                    type="button"
                    className={`flex w-full items-center text-sm font-normal hover:bg-[#F7F8F9] min-h-6 px-4 py-3 ${
                      selectedSku === skuId
                        ? 'text-primaryBlue font-semibold bg-[#F7F8F9]'
                        : 'text-restructure-primary'
                    }`}
                    onClick={() => {
                      onSelect(skuId)
                      setIsOpen(false)
                    }}
                  >
                    {`${label}: ${optValue}`}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="w-full flex flex-col gap-2">
      {hasNoSpecifications &&
        renderSelectorRow({
          label: 'Tamanho',
          value: 'único',
          hasOnlyOne: true,
          options: [],
        })}

      {!hasNoSpecifications &&
        specKeys.map((specification) => {
          const options =
            availableSpecifications[specification as SkuSpecificationsName]

          const hasOnlyOneOption = options.length === 1
          const displayValue = hasOnlyOneOption
            ? 'único'
            : items
                .find((item) => item.itemId === selectedSku)
                ?.[specification as SkuSpecificationsName]?.[0]?.trim() ??
              'único'

          return renderSelectorRow({
            label: specification,
            value: displayValue,
            hasOnlyOne: hasOnlyOneOption,
            options,
          })
        })}
    </div>
  )
}

export default SkuSelector
